import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { buyerQueriesService } from '../../../services/supabaseService';

const OrdersTab = ({ language }) => {
  const [queries, setQueries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadQueries();
  }, [filterStatus]);

  const loadQueries = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (filterStatus !== 'all') {
        filters.status = filterStatus;
      }
      const data = await buyerQueriesService?.getAll(filters);
      setQueries(data);
    } catch (error) {
      console.error('Failed to load queries:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredQueries = queries?.filter(query => {
    const matchesSearch = query?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         query?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         query?.company?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    return matchesSearch;
  });

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-warning/10 text-warning',
      responded: 'bg-primary/10 text-primary',
      completed: 'bg-success/10 text-success'
    };
    return colors?.[status] || colors?.pending;
  };

  const getStatusLabel = (status) => {
    const labels = {
      en: {
        pending: 'Pending',
        responded: 'Responded',
        completed: 'Completed'
      },
      zh: {
        pending: '待处理',
        responded: '已回复',
        completed: '已完成'
      }
    };
    return labels?.[language]?.[status];
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Intl.DateTimeFormat(language === 'en' ? 'en-US' : 'zh-CN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })?.format(new Date(date));
  };

  const handleStatusChange = async (queryId, newStatus) => {
    try {
      await buyerQueriesService?.updateStatus(queryId, newStatus);
      await loadQueries();
    } catch (error) {
      console.error('Failed to update status:', error);
      alert(language === 'en' ? 'Failed to update status' : '更新状态失败');
    }
  };

  const handleExport = () => {
    try {
      // Prepare CSV data
      const headers = ['Name', 'Email', 'Phone', 'Company', 'Bean', 'Message', 'Status', 'Date', 'Responded At'];
      const csvRows = [
        headers?.join(','),
        ...filteredQueries?.map(query => [
          `"${query?.name || ''}"`,
          `"${query?.email || ''}"`,
          `"${query?.phone || ''}"`,
          `"${query?.company || ''}"`,
          `"${language === 'en' ? query?.bean?.nameEn : query?.bean?.nameZh || ''}"`,
          `"${query?.message?.replace(/"/g, '""') || ''}"`,`"${getStatusLabel(query?.status)}"`,`"${formatDate(query?.createdAt)}"`,`"${query?.respondedAt ? formatDate(query?.respondedAt) : '-'}"`
        ]?.join(','))
      ];
      
      const csvContent = csvRows?.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link?.setAttribute('href', url);
      link?.setAttribute('download', `buyer_queries_${new Date()?.toISOString()?.split('T')?.[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
    } catch (error) {
      console.error('Export failed:', error);
      alert(language === 'en' ? 'Export failed' : '导出失败');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <Input
            type="search"
            placeholder={language === 'en' ? 'Search queries...' : '搜索查询...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full sm:w-64"
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e?.target?.value)}
            className="px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">{language === 'en' ? 'All Status' : '所有状态'}</option>
            <option value="pending">{language === 'en' ? 'Pending' : '待处理'}</option>
            <option value="responded">{language === 'en' ? 'Responded' : '已回复'}</option>
            <option value="completed">{language === 'en' ? 'Completed' : '已完成'}</option>
          </select>
        </div>
        <Button variant="outline" iconName="Download" iconPosition="left" onClick={handleExport}>
          {language === 'en' ? 'Export' : '导出'}
        </Button>
      </div>
      <div className="grid grid-cols-1 gap-4">
        {filteredQueries?.map((query) => (
          <div key={query?.id} className="bg-card border border-border rounded-lg p-5 hover:shadow-md transition-smooth">
            <div className="flex flex-col lg:flex-row lg:items-start gap-4">
              <div className="flex-1 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-foreground">{query?.name}</h3>
                      {query?.isChineseBuyer && (
                        <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded-full">
                          {language === 'en' ? 'Chinese Buyer' : '中国买家'}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{formatDate(query?.createdAt)}</p>
                  </div>
                  <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs font-medium ${getStatusColor(query?.status)}`}>
                    <Icon name={query?.status === 'completed' ? 'CheckCircle2' : query?.status === 'responded' ? 'MessageSquare' : 'Clock'} size={14} />
                    {getStatusLabel(query?.status)}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Icon name="Mail" size={16} color="var(--color-muted-foreground)" />
                      <span className="text-muted-foreground truncate">{query?.email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Icon name="Phone" size={16} color="var(--color-muted-foreground)" />
                      <span className="text-muted-foreground">{query?.phone}</span>
                    </div>
                    {query?.company && (
                      <div className="flex items-center gap-2 text-sm">
                        <Icon name="Building2" size={16} color="var(--color-muted-foreground)" />
                        <span className="text-muted-foreground">{query?.company}</span>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    {query?.bean && (
                      <div className="flex items-center gap-2 text-sm">
                        <Icon name="Coffee" size={16} color="var(--color-primary)" />
                        <span className="text-foreground font-medium">{language === 'en' ? query?.bean?.nameEn : query?.bean?.nameZh}</span>
                      </div>
                    )}
                    {query?.respondedAt && (
                      <div className="flex items-center gap-2 text-sm">
                        <Icon name="Clock" size={16} color="var(--color-success)" />
                        <span className="text-success text-xs">{language === 'en' ? 'Responded:' : '已回复:'} {formatDate(query?.respondedAt)}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-muted/50 rounded-md p-3">
                  <p className="text-sm text-foreground whitespace-pre-line">{query?.message}</p>
                </div>
              </div>

              <div className="flex lg:flex-col gap-2 lg:w-32">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Eye"
                  iconPosition="left"
                  onClick={() => {
                    const subject = encodeURIComponent(`Re: ${query?.bean?.nameEn || 'Your Query'}`);
                    const body = encodeURIComponent(`Dear ${query?.name},\n\nThank you for your inquiry regarding ${query?.bean?.nameEn || 'our coffee'}.\n\n`);
                    window.open(`mailto:${query?.email}?subject=${subject}&body=${body}`, '_blank');
                  }}
                  fullWidth
                >
                  {language === 'en' ? 'View' : '查看'}
                </Button>
                {query?.status === 'pending' && (
                  <Button
                    variant="default"
                    size="sm"
                    iconName="Send"
                    iconPosition="left"
                    onClick={() => {
                      const subject = encodeURIComponent(`Re: ${query?.bean?.nameEn || 'Your Query'}`);
                      const body = encodeURIComponent(`Dear ${query?.name},\n\nThank you for your inquiry.\n\n`);
                      window.open(`mailto:${query?.email}?subject=${subject}&body=${body}`, '_blank');
                      handleStatusChange(query?.id, 'responded');
                    }}
                    fullWidth
                  >
                    {language === 'en' ? 'Respond' : '回复'}
                  </Button>
                )}
                {query?.status === 'responded' && (
                  <Button
                    variant="success"
                    size="sm"
                    iconName="CheckCircle2"
                    iconPosition="left"
                    onClick={() => handleStatusChange(query?.id, 'completed')}
                    fullWidth
                  >
                    {language === 'en' ? 'Complete' : '完成'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {filteredQueries?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Search" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
          <p className="text-muted-foreground">{language === 'en' ? 'No queries found' : '未找到查询'}</p>
        </div>
      )}
    </div>
  );
};

export default OrdersTab;